import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Disclaimer · Surer Adda With Sanchayeeta" },
      { name: "description", content: "Disclaimer for content, bookings, external links and advertisements on Surer Adda With Sanchayeeta." },
      { property: "og:title", content: "Disclaimer · Surer Adda With Sanchayeeta" },
      { property: "og:description", content: "General information, external links, booking and advertisement disclaimer for Surer Adda With Sanchayeeta." },
      { property: "og:url", content: `${SITE.url}/disclaimer` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/disclaimer` }],
  }),
  component: DisclaimerPage,
});

function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="font-script text-4xl text-primary">Disclaimer</p>
        <p className="mt-1 text-sm text-muted-foreground">Last updated: June 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-foreground text-sm leading-relaxed">
          <p>
            The information provided on Surer Adda With Sanchayeeta is published in good faith and
            for general information, entertainment, promotional, and communication purposes only.
            While we strive to keep the content accurate, relevant, and updated, we do not make any
            warranties or guarantees regarding the completeness, reliability, or accuracy of any
            information published on this website.
          </p>
          <p>
            Any action you take based on the information found on this website is strictly at your
            own discretion and risk. Surer Adda With Sanchayeeta will not be liable for any losses,
            damages, or inconvenience arising from the use of our website or reliance on any
            information published here.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Content Purpose</h2>
          <p>The content available on this website may include:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Information about live singing performances</li>
            <li>Music-related posts, song covers, and performance updates</li>
            <li>Booking information and event-related communication</li>
            <li>General website content, announcements, and audience engagement materials</li>
          </ul>
          <p>
            This content is intended for general informational, artistic, promotional, and
            entertainment use only.
          </p>

          <h2 className="mt-6 text-lg font-semibold">External Links Disclaimer</h2>
          <p>
            Our website may contain links to external websites, social media pages, or third-party
            platforms for additional information, promotions, or convenience. While we aim to link
            only to useful and reputable sources, we have no control over the content, policies,
            availability, or nature of those third-party websites.
          </p>
          <p>
            The inclusion of any external link does not imply endorsement or recommendation of all
            content found on those websites. Visitors should use their own judgment and review the
            privacy policies and terms of third-party websites before interacting with them.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Professional and Booking Information</h2>
          <p>
            Any information related to event bookings, performance enquiries, schedules, pricing
            discussions, or communication shared on this website is subject to change without prior
            notice. Final performance arrangements, booking confirmations, availability, and
            service details should always be discussed and confirmed directly through official
            communication channels.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Advertisement Disclaimer</h2>
          <p>
            This website may display advertisements, sponsored content, affiliate links, or
            third-party promotional material in the future, including ads served by services such
            as Google AdSense. We do not guarantee the quality, accuracy, or reliability of
            products, services, or claims made by third-party advertisers.
          </p>
          <p>
            Users are encouraged to verify information independently before making any decisions
            based on advertisements or third-party content.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Consent</h2>
          <p>By using this website, you agree to this Disclaimer and accept its terms.</p>

          <h2 className="mt-6 text-lg font-semibold">Contact</h2>
          <p>
            If you have any questions about this Disclaimer, please contact us through the
            information provided on our{" "}
            <a className="text-primary hover:underline" href="/contact">Contact Us</a> page.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
